import { animated, useSpring } from '@react-spring/three';
import { useGLTF } from '@react-three/drei';
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  BoltClosePos,
  BoltOpenPosBack,
  BoltOpenPosFront,
  Deg0,
  DoorDeg90CCW,
  HandleDeg90CW,
  Scale0,
  Scale1,
} from '../../utils/AnimaionSprings';
import CurrentContext from '../../utils/CurrentContext';
import useChangedTexture from '../../utils/customHooks/useChangedTexture';
import useIsMobile from '../../utils/customHooks/useIsMobile';
import useTextureCustom from '../../utils/customHooks/useTextureCustom';
import {
  BlackChrome,
  BlackPlastic,
  BoltMaterial,
  Chrome3,
  PaintMaterial,
} from '../../utils/finsishesData';
import { handleExportGLTF } from '../../utils/jsHelper';
import { LockMechanical, LockSecuramToplit } from './model_locks';

//region Default options
const defaultoptions = [
  {
    materialName: 'body_paint',
    finishToApply: 'texturedblack',
  },
  {
    materialName: 'interior_fabric',
    finishToApply: 'grayfabric',
  },
  {
    materialName: 'securam_topit_lock',
    finishToApply: 'Lock_securam_toplit',
  },
  {
    materialName: 'mechanical_lock',
    finishToApply: 'Lock_mechanical',
  },
];

const HandleUpdateTexture = (item, materials) => {
  useTextureCustom(materials[item?.materialName], item?.finishToApply);
};

export function Centurion24({ lockType, currentVariant }) {
  const isMobile = useIsMobile();
  const { nodes, materials, scene } = useGLTF(
    isMobile ? 'models/centurion24-m.glb' : 'models/centurion24.glb',
  );
  const mobileModel = useGLTF('models/centurion24-m.glb');

  if (isMobile) {
    return (
      <Model
        {...{
          nodes,
          materials,
          scene,
          currentVariant,
          lockType,
          mobileProp: true,
        }}
      />
    );
  } else {
    return (
      <>
        <Model
          {...{
            nodes,
            materials,
            scene,
            currentVariant,
            lockType,
          }}
        />
        <Model
          {...{
            nodes: mobileModel?.nodes,
            materials: mobileModel?.materials,
            scene: mobileModel?.scene,
            currentVariant,
            lockType,
            visible: false,
            mobileProp: true,
          }}
        />
      </>
    );
  }
}

const Model = ({
  nodes,
  materials,
  scene,
  currentVariant,
  lockType,
  visible = true,
  mobileProp = false,
}) => {
  const {
    setLoading,
    setIsARReady,
    setARModelSrc,
    setARUnicode,
    isAR,
    setIsAR,
    doorOpen,
    colorVariant,
  } = useContext(CurrentContext);

  //region animation springs
  const { rotation_Door } = useSpring({
    rotation_Door: doorOpen ? DoorDeg90CCW : Deg0,
    config: { duration: 2000 },
    delay: 1250,
  });

  const { handle_Rotation1 } = useSpring({
    handle_Rotation1: doorOpen ? HandleDeg90CW : Deg0,
    config: { duration: 1000 },
    delay: doorOpen ? 0 : 3250,
  });

  const { handle_Rotation2 } = useSpring({
    handle_Rotation2: doorOpen ? Deg0 : HandleDeg90CW,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { handle_Scale1 } = useSpring({
    handle_Scale1: doorOpen ? Scale0 : Scale1,
    config: { duration: 1 },
    delay: 2250,
  });

  const { handle_Scale2 } = useSpring({
    handle_Scale2: doorOpen ? Scale1 : Scale0,
    config: { duration: 1 },
    delay: 2250,
  });

  const { position_Bolt_Front } = useSpring({
    position_Bolt_Front: doorOpen ? BoltOpenPosFront : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { position_Bolt_Back } = useSpring({
    position_Bolt_Back: doorOpen ? BoltOpenPosBack : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  useEffect(() => {
    if (isAR && mobileProp) {
      handleExportGLTF({
        scene,
        fileName: 'centurion24',
        setLoading,
      }).then(({ file, unicode } = {}) => {
        setARModelSrc(file);
        setIsARReady(true);
        setARUnicode(unicode);
      });
    }
    setIsAR(false);
  }, [isAR, mobileProp]);

  // defaultoptions.map((item) => {
  //   HandleUpdateTexture(item, materials);
  // });
  useTextureCustom(materials.interior_fabric, 'grayfabric');
  // useTextureCustom(materials.securam_topit_lock, 'Lock_securam_toplit');
  useTextureCustom(materials.mechanical_lock, 'Lock_mechanical');
  const materialNames = {
    interior_materials: 'interior_fabric',
    outer_materials: 'body_paint',
    hardware_materials: [
      'wheel_handle',
      'securam_topit_lock',
      // 'mechanical_lock',
    ],
  };

  // useChangedTexture(
  //   currentVariant,
  //   materials,
  //   materialNames,
  //   HandleUpdateTexture,
  // );
  useTextureCustom(PaintMaterial, currentVariant?.outer || 'texturedblack');

  const decalOuterColor = '#ffffff';
  // currentVariant.hardware === "Chrome3hardware"
  // ? "#515151"
  // : currentVariant.hardware === "chromehardware"
  // ? "#e5e5e5"
  // : currentVariant.hardware === "brasshardware"
  // ? "#C1A163"
  // : "#515151";

  materials.door_decal.color.set(decalOuterColor);

  return (
    <group dispose={null} visible={visible}>
      <animated.group position={[0.314, 0.066, 0.269]} rotation={rotation_Door}>
        <animated.mesh
          position={position_Bolt_Back}
          castShadow
          geometry={nodes.bolt_back.geometry}
          material={BoltMaterial}
        />
        <animated.mesh
          position={position_Bolt_Front}
          castShadow
          geometry={nodes.bolt_front.geometry}
          material={BoltMaterial}
        />
        <mesh
          castShadow
          geometry={nodes.bolts_housing.geometry}
          material={BlackPlastic}
        />
        <group position={[-0.31, 1.0475, -0.014]}>
          <LockMechanical
            material={materials.mechanical_lock}
            scale={lockType === 'mechanicallock' ? [1, 1, 1] : [0, 0, 0]}
          />
          <LockSecuramToplit
          // colorVariant?.outer === "texturedwhite" ? BlackChrome : Chrome3
            // material={colorVariant?.threeSpoke ? Chrome3 : BlackChrome}
            material={colorVariant?.outer === "texturedwhite"? BlackChrome: colorVariant?.threeSpoke? Chrome3: BlackChrome}
            scale={lockType === 'securamtoplit' ? [1, 1, 1] : [0, 0, 0]}
          />
        </group>
        {/* // handle  */}
        <group position={[-0.314, 0.834, -0.013]} visible={!colorVariant?.threeSpoke}>
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.handle_1.geometry}
            material={materials.handle}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.handle_2.geometry}
            material={materials['Handle_Plane.001']}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.handle_3.geometry}
            material={materials.Single_Handle_Decal}
          />
        </group>
        {/* Door Exterior */}
        <mesh
          castShadow
          geometry={nodes.outer_door.geometry}
          material={PaintMaterial}
        />
        <mesh
          position={[0, 0, 0.01]}
          geometry={nodes.door_decal.geometry}
          material={materials.door_decal}
        />

        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_1.geometry}
          material={materials.Pockets_Door}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_2.geometry}
          material={materials.Door_Pockets_Borders}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_3.geometry}
          material={materials.Door_Pocket_Nets}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_4.geometry}
          material={materials.Door_Zipper_Line}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_5.geometry}
          material={materials.Door_Zippers}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_6.geometry}
          material={materials.InnerDoor_Decal}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_7.geometry}
          material={materials.Door_Bag_Strap}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_8.geometry}
          material={materials.Normal_Pocket_Base}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_9.geometry}
          material={materials.Door_Bolts_Right}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_10.geometry}
          material={materials.Door_Bolts_Housing}
        />
        <group visible={colorVariant?.threeSpoke}>
        <animated.group
          position={[-0.314, 0.834, 0.038]}
          rotation={handle_Rotation1}
          scale={handle_Scale1}
        >
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_1.geometry}
            // material={materials.wheel_handle}
            material={colorVariant?.outer === "texturedwhite" ? BlackChrome : Chrome3}
          />
          <mesh
            position={[0, 0, 0.015]}
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_2.geometry}
            material={materials.Handle_Plane}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_3.geometry}
            material={materials.Wheel_Decal}
          />
        </animated.group>

        <animated.group
          position={[-0.314, 0.834, 0.038]}
          rotation={handle_Rotation2}
          scale={handle_Scale2}
        >
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_1.geometry}
            // material={materials.wheel_handle}
            material={colorVariant?.outer === "texturedwhite" ? BlackChrome : Chrome3}
          />
          <mesh
            position={[0, 0, 0.015]}
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_2.geometry}
            material={materials.Handle_Plane}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_3.geometry}
            material={materials.Wheel_Decal}
          />
        </animated.group>
        </group>
      </animated.group>

      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior.geometry}
        material={materials.interior_fabric}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_1.geometry}
        material={materials.Door_Bolts_Housing}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_2.geometry}
        material={materials.Bars}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_3.geometry}
        material={materials.Shelves_2_Horizontal}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_4.geometry}
        material={materials.Door_Bolts_Right}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.outer_body_1.geometry}
        material={PaintMaterial}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.outer_body_2.geometry}
        material={BlackPlastic}
      />
    </group>
  );
};

//useGLTF.preload("models/centurion24.glb");